import React, { useEffect } from 'react';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';

import styled from 'styled-components/macro';
import { ResourceStrings } from '../../../ResourceStrings';
import { cssVarStr } from '../../../utils/react';
import { darken } from 'polished';
import { ChildrenProps } from '../../../utils/types';
import Button from '../../shared/Button';
import { App, SignInState } from '../../../app';

export interface AuthenticationMessagesProps {
    signinState: SignInState;
    signInErrorMessage: string;
    render: (props: ChildrenProps) => JSX.Element;
}

const withProgress = (label: string, key: string): JSX.Element => <div key={key}>
    <ProgressIndicator />
    <div className="message">{label}</div>
</div>

export function AuthenticationMessages({ signinState, signInErrorMessage, render }: AuthenticationMessagesProps): JSX.Element {
    const messagesToRead: string[] = [];
    const messagesToRender: JSX.Element[] = [];

    const addMessage = (condition: boolean, text: string, element: JSX.Element): void => {
        if (condition) {
            messagesToRead.push(text);
            messagesToRender.push(element);
        }
    }

    addMessage(
        [SignInState.Expired, SignInState.Failed, SignInState.MicrosoftAccountRequired, SignInState.Prompt].indexOf(signinState) !== -1,
        ResourceStrings.authNeeded(),
        <div key="authNeeded_ReportProblem" className="message auth-needed">
            <div>{ResourceStrings.authNeeded()}</div>
            <Button onClick={() => App.requestSignOutAndIn()}>{ResourceStrings.signIn()}</Button>
        </div>
    );

    addMessage(signinState === SignInState.Checking, ResourceStrings.signInCheck(), withProgress(ResourceStrings.signInCheck(), "signInCheck"));
    addMessage(signinState === SignInState.Executing, ResourceStrings.signInExecuting(), withProgress(ResourceStrings.signInExecuting(), "signInExecuting"));
    addMessage(signinState === SignInState.Failed, ResourceStrings.signInError(signInErrorMessage), <div key="signInErrorMessage" className="message error">{ResourceStrings.signInError(signInErrorMessage)}</div>);
    addMessage(signinState === SignInState.Expired, ResourceStrings.signInExpired(), <div key="signInExpired" className="message">{ResourceStrings.signInExpired()}</div>);
    addMessage(signinState === SignInState.MicrosoftAccountRequired, ResourceStrings.signInWithMicrosoftAccount(), <div key="signInWithMicrosoftAccount" className="message">{ResourceStrings.signInWithMicrosoftAccount()}</div>);

    if (messagesToRead.length !== 0) {
        App.read(messagesToRead.join('. '));
    }

    useEffect(() => {
        if (signinState === SignInState.Success) {
            App.read(ResourceStrings.authenticated());
        }
    }, [signinState]);

    return messagesToRender.length !== 0 && render({
        children: <NotificationWrapper>
            {messagesToRender}
        </NotificationWrapper>
    })
}

export function Notification({ children }: ChildrenProps): JSX.Element {
    return children && <NotificationWrapper>{children}</NotificationWrapper>
}

const NotificationWrapper = styled.div`
    border-radius: 2px;
    border: solid 1px ${darken(0.15, cssVarStr("--color-background"))};
    font-size: 1.8rem;

    .ms-ProgressIndicator-itemProgress {
        padding: 0;
    }

    .message {
        padding: 2rem 4rem;
    }

    .error {
        color: var(--color-error);
    }

    .auth-needed {
        > * + * {
            margin-top: 2rem;
        }
    }
`;
